import React, { useState, useEffect, useRef } from 'react';
import { MapPin, X } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { createCustomIcon } from '../utils/mapHelpers';
import { primaryButtonStyle, secondaryButtonStyle, applyDisabledStyle, applyIconOnlyStyle, neutralButtonStyle } from '../utils/buttonStyles';
import { 
  getSelectStyle, 
  getInputStyle, 
  getTextareaStyle, 
  labelStyle, 
  errorMessageStyle, 
  formContainerStyle,
  formActionsStyle,
  formGroupStyle
} from '../utils/formStyles';

// Componente para selecionar localização no mapa
const LocationSelector = ({ position, setPosition, propriedadePosition }) => {
  const map = useMapEvents({
    click: (e) => {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  // Centralizar o mapa na propriedade quando o componente for montado
  useEffect(() => {
    if (propriedadePosition && map) {
      map.setView(propriedadePosition, 15);
    }
  }, [propriedadePosition, map]);

  return (
    <>
      {/* Marcador da residência */}
      {position && (
        <Marker 
          position={position} 
          icon={createCustomIcon('Em Análise', 'residencia')}
        />
      )}
      
      {/* Marcador da propriedade */}
      {propriedadePosition && (
        <Marker 
          position={propriedadePosition} 
          icon={createCustomIcon('Mapeada', 'propriedade')}
        />
      )}
    </>
  );
};

/**
 * Componente para cadastro e edição de residências
 */
const CadastroResidencia = ({ propriedadeId, residencia, onClose, onSave }) => {
  const { salvarResidencia, loading, propriedades } = useFossasSepticas();
  
  // Estado do formulário
  const [formData, setFormData] = useState({
    propriedade_id: propriedadeId,
    identificacao: '',
    numero_moradores: 1,
    latitude: null,
    longitude: null,
    status: 'Não Mapeada',
    observacoes: ''
  });

  // Estado para erros de validação
  const [errors, setErrors] = useState({});
  
  // Estado para posição no mapa
  const [position, setPosition] = useState(null);
  
  // Estado para posição da propriedade
  const [propriedadePosition, setPropriedadePosition] = useState(null);
  
  // Centro inicial do mapa (baseado na propriedade ou em Conceição do Castelo - ES)
  const centroConceicaoCastelo = [-20.359292, -41.247282];
  const [mapCenter, setMapCenter] = useState(centroConceicaoCastelo);
  const [zoom, setZoom] = useState(15);

  // Carregar dados da propriedade para centralizar o mapa e mostrar marcador
  useEffect(() => {
    if (propriedades.length > 0) {
      const propriedade = propriedades.find(p => p.id === propriedadeId);
      if (propriedade && propriedade.latitude && propriedade.longitude) {
        const propPosition = [propriedade.latitude, propriedade.longitude];
        
        // Definir o centro do mapa e a posição da propriedade
        setMapCenter(propPosition);
        setPropriedadePosition(propPosition);
        
        // Se for um novo cadastro (não edição), sugerir a posição da propriedade como ponto de partida
        if (!residencia) {
          setPosition(propPosition);
        }
        
        // Definir um zoom adequado
        setZoom(16);
      }
    }
  }, [propriedades, propriedadeId, residencia]);

  // Carregar dados da residência se for edição
  useEffect(() => {
    if (residencia) {
      setFormData({
        id: residencia.id,
        propriedade_id: propriedadeId,
        identificacao: residencia.identificacao,
        numero_moradores: residencia.numero_moradores,
        latitude: residencia.latitude,
        longitude: residencia.longitude,
        status: residencia.status,
        observacoes: residencia.observacoes || ''
      });
      
      // Atualizar posição no mapa
      if (residencia.latitude && residencia.longitude) {
        const pos = [residencia.latitude, residencia.longitude];
        setPosition(pos);
        setMapCenter(pos);
      }
    }
  }, [residencia, propriedadeId]);

  // Atualizar estado do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'numero_moradores' ? parseInt(value) : value
    }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  // Atualizar posição no mapa
  useEffect(() => {
    if (position) {
      setFormData(prev => ({
        ...prev,
        latitude: position[0],
        longitude: position[1]
      }));
    }
  }, [position]);

  // Validar formulário
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.identificacao.trim()) {
      newErrors.identificacao = 'Identificação da residência é obrigatória';
    }
    
    if (formData.numero_moradores < 1) {
      newErrors.numero_moradores = 'Número de moradores deve ser pelo menos 1';
    }
    
    if (!formData.latitude || !formData.longitude) {
      newErrors.location = 'Selecione a localização no mapa';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Enviar formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const success = await salvarResidencia(formData);
      
      if (success && onSave) {
        onSave();
      }
    }
  };

  if (!mapCenter) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Carregando dados da propriedade...</p>
      </div>
    );
  }

  return (
    <div style={formContainerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', borderBottom: '1px solid #e9ecef' }}>
        <h2 style={{ margin: 0, fontSize: '18px' }}>{residencia ? 'Editar' : 'Nova'} Residência</h2>
        <button 
          style={applyIconOnlyStyle(neutralButtonStyle)} 
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      
      <form onSubmit={handleSubmit} style={{ padding: '20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px', marginBottom: '20px' }}>
          <div style={formGroupStyle}>
            <label style={labelStyle}>Identificação</label>
            <input
              type="text"
              name="identificacao"
              value={formData.identificacao}
              onChange={handleChange}
              style={getInputStyle({ error: !!errors.identificacao })}
              placeholder="Ex: Casa Principal, Casa 1, etc."
            />
            {errors.identificacao && <div style={errorMessageStyle}>{errors.identificacao}</div>}
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Número de Moradores</label>
            <input
              type="number"
              name="numero_moradores"
              value={formData.numero_moradores}
              onChange={handleChange}
              min="1"
              style={getInputStyle({ error: !!errors.numero_moradores })}
            />
            {errors.numero_moradores && <div style={errorMessageStyle}>{errors.numero_moradores}</div>}
          </div>
          
          <div style={formGroupStyle}>
            <label style={labelStyle}>Status</label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              style={getSelectStyle()}
            >
              <option value="Não Mapeada">Não Mapeada</option>
              <option value="Mapeada">Mapeada</option>
              <option value="Em Análise">Em Análise</option>
            </select>
          </div>
        </div>
        
        <div style={formGroupStyle}>
          <label style={labelStyle}>Observações (opcional)</label>
          <textarea
            name="observacoes"
            value={formData.observacoes}
            onChange={handleChange}
            style={getTextareaStyle()}
            placeholder="Informações adicionais sobre a residência"
          />
        </div>
        
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '16px',
            marginBottom: '10px'
          }}>
            <MapPin size={16} style={{ marginRight: '5px' }} />
            Localização da Residência
          </h3>
          <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
            Clique no mapa para selecionar a localização exata da residência. O marcador verde indica a localização da propriedade.
          </p>
          
          {errors.location && <div style={{ 
            ...errorMessageStyle,
            marginBottom: '10px',
            padding: '8px 12px',
            backgroundColor: '#f8d7da',
            borderRadius: '4px'
          }}>{errors.location}</div>}
          
          <div style={{ 
            height: '300px',
            borderRadius: '8px',
            overflow: 'hidden',
            marginBottom: '10px',
            border: '1px solid #ced4da'
          }}>
            <MapContainer 
              key={propriedadePosition ? `${propriedadePosition[0]}-${propriedadePosition[1]}` : 'default'} 
              center={mapCenter} 
              zoom={zoom} 
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              <LocationSelector 
                position={position} 
                setPosition={setPosition} 
                propriedadePosition={propriedadePosition} 
              />
            </MapContainer>
          </div>
          
          {position && (
            <div style={{ 
              padding: '10px 15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '4px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center'
            }}>
              <MapPin size={16} style={{ marginRight: '8px', color: '#2e7d32' }} />
              <strong>Coordenadas:</strong>&nbsp;{position[0].toFixed(6)}, {position[1].toFixed(6)}
            </div>
          )}
        </div>
        
        <div style={formActionsStyle}>
          <button 
            type="button" 
            style={secondaryButtonStyle}
            onClick={onClose}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            style={applyDisabledStyle(primaryButtonStyle, loading.residencias)}
            disabled={loading.residencias}
          >
            {loading.residencias ? 'Salvando...' : residencia ? 'Atualizar' : 'Cadastrar'}
          </button>
        </div>
      </form>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .map-container {
          height: 300px;
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 10px;
        }
      `}</style>
    </div>
  );
};

export default CadastroResidencia;